import React, { useEffect } from 'react';
import HelpLayout from '../../../components/help/HelpLayout';
import {
  UserIcon,
  CameraIcon,
  AcademicCapIcon,
  MapPinIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const ProfileHelp = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { name: 'User Guide', href: '/help/getting-started' },
    { name: 'Profile Management' }
  ];

  const profileSections = [
    {
      title: 'Profile Picture Upload',
      icon: CameraIcon,
      description: 'Upload and manage your profile picture with enhanced features',
      features: [
        'Hover over your profile picture to see upload requirements',
        'Supported formats: JPEG, PNG, WebP (maximum 5MB)',
        'Technical requirements appear only when needed',
        'Easy drag-and-drop or click to upload',
        'Remove or change your picture anytime'
      ]
    },
    {
      title: 'Personal Information',
      icon: UserIcon,
      description: 'Manage your personal details and contact information',
      features: [
        'First name and last name (required)',
        'Email address with validation',
        'Phone number (10 digits, supports +27 format)',
        'South African ID number with auto-fill features',
        'Date of birth and gender (auto-filled from ID)',
        'Race and address information'
      ]
    },
    {
      title: 'Educational Background',
      icon: AcademicCapIcon,
      description: 'Add your educational qualifications and history',
      features: [
        'Highest grade completed (Grade 8-12)',
        'Matric year and school name',
        'Tertiary qualifications (Certificate to Doctorate)',
        'Institution and field of study',
        'Year completed and status',
        'Support for multiple qualifications'
      ]
    },
    {
      title: 'Location Services',
      icon: MapPinIcon,
      description: 'Set your location for course allocation and services',
      features: [
        'Postal code for economic corridor allocation',
        'Automatic corridor assignment (North, South, East, West, Central)',
        'GPS location sharing (optional)',
        'Privacy controls for location data',
        'City and province selection'
      ]
    }
  ];

  return (
    <HelpLayout title="Profile Management" breadcrumbs={breadcrumbs}>
      <div className="help-content">
        <div className="card">
          <div className="card-body">
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              Profile Management Guide
            </h2>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6,
              marginBottom: '32px'
            }}>
              Learn how to manage your personal information, educational background, and account settings.
              A complete profile is required for course applications.
            </p>

            {/* Profile Picture Section */}
            <div style={{
              background: 'var(--primary-yellow-light)',
              border: '2px solid var(--primary-yellow)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '32px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: 'var(--primary-black)',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <CameraIcon style={{ width: '20px', height: '20px' }} />
                New: Enhanced Profile Picture Upload
              </h3>
              <p style={{
                fontSize: '14px',
                color: 'var(--primary-black)',
                margin: 0,
                lineHeight: 1.5
              }}>
                We've improved the profile picture upload experience! Now when you hover over your profile picture,
                you'll see the file requirements (formats and size limits) right when you need them, keeping your
                profile page clean and organized.
              </p>
            </div>

            {/* Profile Sections */}
            {profileSections.map((section, index) => {
              const Icon = section.icon;
              return (
                <div key={index} style={{
                  border: '1px solid var(--border-gray)',
                  borderRadius: '12px',
                  marginBottom: '24px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    background: 'var(--background-gray)',
                    padding: '16px 20px',
                    borderBottom: '1px solid var(--border-gray)'
                  }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Icon style={{ width: '20px', height: '20px', color: 'var(--primary-yellow)' }} />
                      {section.title}
                    </h3>
                  </div>
                  <div style={{ padding: '20px' }}>
                    <p style={{
                      fontSize: '14px',
                      color: 'var(--text-gray)',
                      marginBottom: '16px',
                      lineHeight: 1.5
                    }}>
                      {section.description}
                    </p>
                    <ul style={{
                      margin: 0,
                      paddingLeft: '20px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}>
                      {section.features.map((feature, featureIndex) => (
                        <li key={featureIndex} style={{
                          fontSize: '14px',
                          color: 'var(--text-dark)',
                          lineHeight: 1.4
                        }}>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}

            {/* Tips and Best Practices */}
            <div className="card" style={{ marginTop: '32px' }}>
              <div className="card-body">
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <InformationCircleIcon style={{ width: '20px', height: '20px', color: 'var(--primary-yellow)' }} />
                  Tips & Best Practices
                </h3>

                <div style={{
                  display: 'grid',
                  gap: '16px',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))'
                }}>
                  <div style={{
                    background: '#EFF6FF',
                    border: '1px solid #DBEAFE',
                    borderRadius: '8px',
                    padding: '16px'
                  }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#1E40AF',
                      marginBottom: '8px'
                    }}>
                      Profile Completion
                    </h4>
                    <p style={{
                      fontSize: '13px',
                      color: '#374151',
                      margin: 0,
                      lineHeight: 1.4
                    }}>
                      Complete all profile sections before applying for courses. Incomplete profiles may delay application processing.
                    </p>
                  </div>

                  <div style={{
                    background: '#F0FDF4',
                    border: '1px solid #BBF7D0',
                    borderRadius: '8px',
                    padding: '16px'
                  }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#166534',
                      marginBottom: '8px'
                    }}>
                      ID Number Benefits
                    </h4>
                    <p style={{
                      fontSize: '13px',
                      color: '#374151',
                      margin: 0,
                      lineHeight: 1.4
                    }}>
                      Enter your SA ID number to automatically fill date of birth and gender fields. This speeds up profile completion.
                    </p>
                  </div>

                  <div style={{
                    background: '#FFFBEB',
                    border: '1px solid #FED7AA',
                    borderRadius: '8px',
                    padding: '16px'
                  }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#92400E',
                      marginBottom: '8px'
                    }}>
                      Location Accuracy
                    </h4>
                    <p style={{
                      fontSize: '13px',
                      color: '#374151',
                      margin: 0,
                      lineHeight: 1.4
                    }}>
                      Provide accurate postal code and location details for proper economic corridor allocation and course recommendations.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div style={{
              background: 'var(--background-gray)',
              borderRadius: '12px',
              padding: '24px',
              marginTop: '32px',
              textAlign: 'center'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                Ready to Update Your Profile?
              </h3>
              <p style={{
                fontSize: '14px',
                color: 'var(--text-gray)',
                marginBottom: '20px'
              }}>
                Keep your information current to receive the best course recommendations and ensure smooth application processing.
              </p>
              <a
                href="/profile"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'var(--primary-yellow)',
                  color: 'var(--primary-black)',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(255, 193, 7, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <UserIcon style={{ width: '16px', height: '16px' }} />
                Go to Profile
              </a>
            </div>
          </div>
        </div>
      </div>
    </HelpLayout>
  );
};

export default ProfileHelp;
